import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, RefreshCw, ChevronLeft, ChevronRight, Trash2, Pencil, Plus, Database, FileText, AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 25;

const truncate = (v, n = 80) => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v.length > n ? v.slice(0, n) + "…" : v;
  if (typeof v === "object") {
    try {
      const s = JSON.stringify(v);
      return s.length > n ? s.slice(0, n) + "…" : s;
    } catch {
      return "[object]";
    }
  }
  return String(v);
};

const prettify = (key) => key.replaceAll("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Generic MongoDB browser. Admin-only. Primary admin required for writes.
 */
export const AdminDatabaseSection = () => {
  const { user, authHeader } = useAuth();
  const isPrimary = !!user?.is_primary_admin;

  const [collections, setCollections] = useState([]);
  const [coll, setColl] = useState("");
  const [docs, setDocs] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  // Edit / create dialog
  const [editor, setEditor] = useState(null); // { mode: 'edit'|'create', doc, jsonText }
  const [confirmDel, setConfirmDel] = useState(null);

  const loadCollections = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/db/collections`, { headers: authHeader() });
      setCollections(r.data || []);
      if (!coll && r.data?.length) setColl(r.data[0].name);
    } catch (e) {
      toast.error("No se pudo listar las colecciones");
    }
  }, [authHeader, coll]);

  const loadDocs = useCallback(
    async (c = coll, s = skip, q = search.trim()) => {
      if (!c) return;
      setBusy(true);
      try {
        const params = new URLSearchParams({ skip: String(s), limit: String(PAGE_SIZE) });
        if (q) params.set("q", q);
        const r = await axios.get(`${API}/admin/db/${c}/docs?${params.toString()}`, { headers: authHeader() });
        setDocs(r.data?.docs || []);
        setTotal(r.data?.total || 0);
      } catch {
        setDocs([]);
        setTotal(0);
      } finally {
        setBusy(false);
      }
    },
    [authHeader, coll, search, skip],
  );

  useEffect(() => {
    loadCollections();
  }, [loadCollections]);

  useEffect(() => {
    if (coll) {
      setSkip(0);
      loadDocs(coll, 0, search);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coll]);

  const openEdit = async (id) => {
    try {
      const r = await axios.get(`${API}/admin/db/${coll}/doc/${id}`, { headers: authHeader() });
      const full = r.data || {};
      const idField = full.id || id;
      const cleaned = { ...full };
      delete cleaned.id;
      setEditor({
        mode: "edit",
        id: idField,
        jsonText: JSON.stringify(cleaned, null, 2),
      });
    } catch {
      toast.error("No se pudo cargar el documento");
    }
  };

  const openCreate = () => {
    setEditor({
      mode: "create",
      id: null,
      jsonText: "{\n  \n}\n",
    });
  };

  const saveEditor = async () => {
    if (!editor) return;
    let parsed;
    try {
      parsed = JSON.parse(editor.jsonText || "{}");
    } catch {
      toast.error("JSON inválido");
      return;
    }
    if (typeof parsed !== "object" || Array.isArray(parsed)) {
      toast.error("Debe ser un objeto JSON");
      return;
    }
    setBusy(true);
    try {
      if (editor.mode === "edit") {
        await axios.patch(
          `${API}/admin/db/${coll}/doc/${editor.id}`,
          { updates: parsed },
          { headers: authHeader() },
        );
        toast.success("Documento actualizado");
      } else {
        await axios.post(`${API}/admin/db/${coll}/doc`, { doc: parsed }, { headers: authHeader() });
        toast.success("Documento creado");
      }
      setEditor(null);
      loadDocs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/db/${coll}/doc/${confirmDel}`, { headers: authHeader() });
      toast.success("Documento eliminado");
      setConfirmDel(null);
      loadDocs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar");
    } finally {
      setBusy(false);
    }
  };

  // Compute column headers from the first doc; cap to 6 columns for the table.
  const headers = (() => {
    const set = new Set(["id"]);
    docs.forEach((d) => Object.keys(d).forEach((k) => set.add(k)));
    return Array.from(set).slice(0, 6);
  })();

  return (
    <div className="space-y-4" data-testid="admin-db-section">
      {!isPrimary && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 text-amber-200 text-xs px-4 py-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> Solo el admin principal puede crear, editar o eliminar documentos. Tú tienes acceso de solo lectura.
        </div>
      )}

      {/* Toolbar */}
      <div className="glass-dark rounded-2xl p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-white">
          <Database className="w-4 h-4 text-[#d4af37]" />
          <span className="font-serif text-base">Base de Datos</span>
        </div>
        <div className="flex-1 min-w-[180px]">
          <Select value={coll} onValueChange={setColl}>
            <SelectTrigger data-testid="db-coll-picker" className="bg-black/40 border-white/10 text-white">
              <SelectValue placeholder="Selecciona colección" />
            </SelectTrigger>
            <SelectContent className="bg-[#0a0a0a] border-[#d4af37]/30 text-white max-h-80">
              {collections.map((c) => (
                <SelectItem key={c.name} value={c.name}>
                  {c.name} ({c.count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Input
            data-testid="db-search"
            placeholder="Buscar por ID exacto"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setSkip(0);
                loadDocs(coll, 0, search);
              }
            }}
            className="bg-black/40 border-white/10 text-white w-52"
          />
          <button
            type="button"
            data-testid="db-refresh"
            onClick={() => loadDocs()}
            className="px-3 py-2 rounded-md border border-white/10 hover:border-[#d4af37]/50 text-neutral-300 hover:text-white"
            title="Refrescar"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          {isPrimary && (
            <button
              type="button"
              data-testid="db-create-doc"
              onClick={openCreate}
              className="btn-gold px-3 py-2 rounded-md text-xs uppercase tracking-widest inline-flex items-center gap-1"
            >
              <Plus className="w-3.5 h-3.5" /> Nuevo
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="glass-dark rounded-2xl overflow-hidden">
        {busy ? (
          <div className="p-10 text-center"><Loader2 className="w-6 h-6 text-[#d4af37] animate-spin mx-auto" /></div>
        ) : docs.length === 0 ? (
          <div className="p-10 text-center text-sm text-neutral-500 italic">Colección vacía o sin resultados.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="db-docs-table">
              <thead>
                <tr className="bg-black/40 text-[10px] uppercase tracking-widest text-neutral-400">
                  {headers.map((h) => (
                    <th key={h} className="px-3 py-2 text-left">{prettify(h)}</th>
                  ))}
                  <th className="px-3 py-2 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {docs.map((d) => (
                  <tr key={d.id} className="border-t border-white/5 hover:bg-white/5">
                    {headers.map((h) => (
                      <td key={h} className="px-3 py-2 text-neutral-200 font-mono break-all">
                        {truncate(d[h])}
                      </td>
                    ))}
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button
                        type="button"
                        data-testid={`db-edit-${d.id}`}
                        onClick={() => openEdit(d.id)}
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#d4af37] hover:text-white border border-[#d4af37]/30 hover:border-[#d4af37] rounded-md px-2 py-1 mr-1"
                      >
                        <Pencil className="w-3 h-3" /> Ver / Editar
                      </button>
                      {isPrimary && (
                        <button
                          type="button"
                          data-testid={`db-del-${d.id}`}
                          onClick={() => setConfirmDel(d.id)}
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-red-300 hover:text-white border border-red-500/30 hover:border-red-500 rounded-md px-2 py-1"
                        >
                          <Trash2 className="w-3 h-3" /> Borrar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-3 text-xs text-neutral-400 border-t border-white/5">
            <span>{Math.min(skip + 1, total)}–{Math.min(skip + PAGE_SIZE, total)} de {total}</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid="db-page-prev"
                disabled={skip === 0}
                onClick={() => {
                  const next = Math.max(0, skip - PAGE_SIZE);
                  setSkip(next);
                  loadDocs(coll, next, search);
                }}
                className="px-2 py-1 rounded-md border border-white/10 hover:border-[#d4af37]/50 disabled:opacity-40"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                data-testid="db-page-next"
                disabled={skip + PAGE_SIZE >= total}
                onClick={() => {
                  const next = skip + PAGE_SIZE;
                  setSkip(next);
                  loadDocs(coll, next, search);
                }}
                className="px-2 py-1 rounded-md border border-white/10 hover:border-[#d4af37]/50 disabled:opacity-40"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* JSON Editor */}
      <Dialog open={!!editor} onOpenChange={() => setEditor(null)}>
        <DialogContent className="bg-[#0a0a0a] border border-[#d4af37]/30 text-white max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-serif flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#d4af37]" />
              {editor?.mode === "edit"
                ? <>Editar <span className="font-mono text-xs text-[#d4af37]">{editor.id}</span></>
                : "Nuevo documento"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-[11px] text-neutral-400">
            Edita el documento como JSON. {editor?.mode === "edit"
              ? "Los campos enviados sobrescriben los existentes ($set). No se modifica _id."
              : "Si omites _id, se generará un UUID automáticamente."}
          </p>
          <Textarea
            data-testid="db-editor-json"
            rows={18}
            value={editor?.jsonText || ""}
            onChange={(e) => setEditor((p) => (p ? { ...p, jsonText: e.target.value } : p))}
            className="bg-black/60 border-white/10 text-white font-mono text-xs"
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setEditor(null)}
              className="px-4 py-2 rounded-full border border-white/10 text-neutral-300 hover:text-white text-xs uppercase tracking-widest"
            >
              Cancelar
            </button>
            <button
              type="button"
              data-testid="db-editor-save"
              onClick={saveEditor}
              disabled={busy || !isPrimary}
              className="btn-gold px-5 py-2 rounded-full text-xs uppercase tracking-widest disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Guardar"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!confirmDel} onOpenChange={() => setConfirmDel(null)}>
        <DialogContent className="bg-[#0a0a0a] border border-red-500/30 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-red-300 flex items-center gap-2">
              <Trash2 className="w-4 h-4" /> Eliminar documento
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-neutral-300">
            Estás a punto de eliminar permanentemente el documento <span className="font-mono text-[#d4af37]">{confirmDel}</span> de <span className="text-[#d4af37]">{coll}</span>.
            Esta acción no se puede deshacer.
          </p>
          <div className="flex justify-end gap-2 mt-3">
            <button
              type="button"
              onClick={() => setConfirmDel(null)}
              className="px-4 py-2 rounded-full border border-white/10 text-neutral-300 hover:text-white text-xs uppercase tracking-widest"
            >
              Cancelar
            </button>
            <button
              type="button"
              data-testid="db-delete-confirm"
              onClick={doDelete}
              disabled={busy}
              className="px-5 py-2 rounded-full bg-red-600 hover:bg-red-700 text-white text-xs uppercase tracking-widest disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Eliminar"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDatabaseSection;
